import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { analyticsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard } from './StatCard';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';
import { MoreVertical, Plus } from 'lucide-react';
import { useState } from 'react';

export function DashboardAnalytics() {
  const { user, hasRole } = useAuth();
  const [salesTab, setSalesTab] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [rentTab, setRentTab] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [revenueTab, setRevenueTab] = useState<'daily' | 'weekly' | 'monthly'>('monthly');

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: () => analyticsApi.overview(),
  });

  // Fetch real property counts by type
  const { data: propertyCounts } = useQuery({
    queryKey: ['analytics-property-counts'],
    queryFn: async () => {
      const isAdmin = hasRole('superadmin');
      let saleQuery = supabase.from('properties').select('id', { count: 'exact', head: true }).eq('listing_type', 'sale').eq('approval_status', 'approved');
      let rentQuery = supabase.from('properties').select('id', { count: 'exact', head: true }).eq('listing_type', 'rent').eq('approval_status', 'approved');

      if (!isAdmin && user) {
        saleQuery = saleQuery.eq('user_id', user.id);
        rentQuery = rentQuery.eq('user_id', user.id);
      }

      const [saleRes, rentRes] = await Promise.all([saleQuery, rentQuery]);
      return { sale: saleRes.count || 0, rent: rentRes.count || 0 };
    },
    enabled: !!user,
  });

  // Fetch recent leads as customers
  const { data: recentLeads } = useQuery({
    queryKey: ['analytics-recent-leads'],
    queryFn: async () => {
      let query = supabase.from('leads').select('id, name, status, created_at').order('created_at', { ascending: false }).limit(6);
      if (!hasRole('superadmin') && user) {
        query = query.eq('agent_id', user.id);
      }
      const { data } = await query;
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch lead status distribution for pie chart
  const { data: leadStats } = useQuery({
    queryKey: ['analytics-lead-stats'],
    queryFn: async () => {
      let query = supabase.from('leads').select('status');
      if (!hasRole('superadmin') && user) {
        query = query.eq('agent_id', user.id);
      }
      const { data } = await query;
      const counts: Record<string, number> = { new: 0, contacted: 0, converted: 0, closed: 0 };
      (data || []).forEach((l: any) => { if (l.status && counts[l.status] !== undefined) counts[l.status]++; });
      return counts;
    },
    enabled: !!user,
  });

  // Fetch property views for charts (last 8 weeks)
  const { data: viewsData } = useQuery({
    queryKey: ['analytics-views'],
    queryFn: async () => {
      const eightWeeksAgo = new Date();
      eightWeeksAgo.setDate(eightWeeksAgo.getDate() - 56);
      const { data } = await supabase
        .from('property_views')
        .select('viewed_at')
        .gte('viewed_at', eightWeeksAgo.toISOString())
        .order('viewed_at', { ascending: true });

      // Group by week
      const weeks: Record<string, number> = {};
      (data || []).forEach((v: any) => {
        const d = new Date(v.viewed_at);
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        const key = `W${weekStart.toISOString().slice(5, 10)}`;
        weeks[key] = (weeks[key] || 0) + 1;
      });
      return Object.entries(weeks).slice(-8).map(([week, value]) => ({ week, value }));
    },
    enabled: !!user,
  });

  // Fetch properties created per month for sales chart
  const { data: monthlySales } = useQuery({
    queryKey: ['analytics-monthly-sales'],
    queryFn: async () => {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 8);
      let query = supabase.from('properties').select('created_at, price').eq('listing_type', 'sale').gte('created_at', sixMonthsAgo.toISOString());
      if (!hasRole('superadmin') && user) query = query.eq('user_id', user.id);
      const { data } = await query;

      const months: Record<string, number> = {};
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      (data || []).forEach((p: any) => {
        const m = monthNames[new Date(p.created_at).getMonth()];
        months[m] = (months[m] || 0) + Number(p.price || 0);
      });
      return Object.entries(months).map(([month, value]) => ({ month, value }));
    },
    enabled: !!user,
  });

  if (statsLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const saleCount = propertyCounts?.sale || stats?.total_properties || 0;
  const rentCount = propertyCounts?.rent || stats?.active_listings || 0;
  const totalLeads = leadStats ? Object.values(leadStats).reduce((a, b) => a + b, 0) : 0;

  const barData = (viewsData && viewsData.length > 0)
    ? viewsData.map(v => ({ week: v.week, sold: Math.round(v.value * 0.4), rent: Math.round(v.value * 0.6) }))
    : [{ week: 'No data', sold: 0, rent: 0 }];

  const rentChartData = (viewsData && viewsData.length > 0)
    ? viewsData
    : [{ week: 'No data', value: 0 }];

  const salesChartData = (monthlySales && monthlySales.length > 0)
    ? monthlySales
    : [{ month: 'No data', value: 0 }];

  const pieData = leadStats ? [
    { name: 'New Leads', value: leadStats.new || 1, color: 'hsl(215, 65%, 38%)' },
    { name: 'Contacted', value: leadStats.contacted || 1, color: '#f97316' },
    { name: 'Converted', value: leadStats.converted || 1, color: '#8b5cf6' },
    { name: 'Closed', value: leadStats.closed || 1, color: '#facc15' },
  ] : [];

  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);
  const conversionRate = pieTotal > 0 ? Math.round(((leadStats?.converted || 0) / pieTotal) * 100) : 0;

  const recentCustomers = (recentLeads || []).map((l: any) => ({
    name: l.name,
    type: l.status === 'converted' ? 'CONVERTED' : l.status === 'new' ? 'NEW LEAD' : l.status?.toUpperCase() || 'LEAD',
  }));

  const TabButtons = ({ value, onChange }: { value: string; onChange: (v: any) => void }) => (
    <div className="flex gap-3 text-xs">
      {(['daily', 'weekly', 'monthly'] as const).map(tab => (
        <button key={tab} className={`capitalize pb-1 ${value === tab ? 'border-b-2 border-primary font-semibold text-primary' : 'text-muted-foreground'}`} onClick={() => onChange(tab)}>
          {tab}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard value={saleCount} label="Properties for Sale" variant="sale" progress={saleCount > 0 ? Math.min(100, Math.round((saleCount / Math.max(saleCount + rentCount, 1)) * 100)) : 0} />
        <StatCard value={rentCount} label="Properties for Rent" variant="rent" progress={rentCount > 0 ? Math.min(100, Math.round((rentCount / Math.max(saleCount + rentCount, 1)) * 100)) : 0} />
        <StatCard value={totalLeads} label="Total Leads" variant="customer" progress={totalLeads > 0 ? Math.min(100, Math.round((leadStats?.converted || 0) / totalLeads * 100)) : 0} />
        <StatCard value={saleCount + rentCount} label="Total Listings" variant="city" progress={50} />
      </div>

      {/* Bar Chart + Rent Statistic */}
      <div className="grid lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3 rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Property Views (Sale vs Rent)</h3>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-orange-400" /> Sale <strong className="ml-1">{saleCount}</strong></span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-accent" /> Rent <strong className="ml-1">{rentCount}</strong></span>
                <button className="text-muted-foreground"><MoreVertical className="h-4 w-4" /></button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey="sold" fill="#f97316" radius={[3, 3, 0, 0]} barSize={16} />
                <Bar dataKey="rent" fill="hsl(var(--accent))" radius={[3, 3, 0, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Views Over Time</h3>
              <div className="flex items-center gap-2">
                <TabButtons value={rentTab} onChange={setRentTab} />
                <button className="text-muted-foreground ml-1"><MoreVertical className="h-4 w-4" /></button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={rentChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="value" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.2} strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Sales Statistic */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">Sales Statistic</h3>
            <div className="flex items-center gap-2">
              <TabButtons value={salesTab} onChange={setSalesTab} />
              <button className="text-muted-foreground ml-1"><MoreVertical className="h-4 w-4" /></button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={salesChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => `${v.toLocaleString()} ETB`} />
              <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.1} strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recent Leads + Lead Distribution */}
      <div className="grid lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-2 rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Recent Leads</h3>
              <button className="text-muted-foreground"><MoreVertical className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              {recentCustomers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No leads yet</p>
              ) : recentCustomers.map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                    {c.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className={`text-[10px] font-semibold ${c.type === 'CONVERTED' ? 'text-primary' : 'text-muted-foreground'}`}>{c.type}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-sm">Lead Distribution</h3>
              <button className="text-muted-foreground ml-1"><MoreVertical className="h-4 w-4" /></button>
            </div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-xl font-bold">{totalLeads} Leads</span>
              <span className="inline-flex items-center gap-1 text-primary text-xs font-semibold">
                <span className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-[9px]">↑</span>
                {conversionRate}%
              </span>
              <span className="text-[10px] text-muted-foreground">conversion rate</span>
            </div>
            {pieData.length > 0 && (
              <div className="flex items-center justify-center gap-6">
                <div className="w-40 h-40 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" stroke="none">
                        {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold">{conversionRate}%</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {pieData.map(d => (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                      <span className="text-muted-foreground">{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Progress bars */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5 grid md:grid-cols-3 gap-5">
          {[
            { label: 'Properties for Sale', value: `${saleCount} Units`, pct: saleCount > 0 ? Math.min(100, Math.round(saleCount / Math.max(saleCount + rentCount, 1) * 100)) : 0, color: 'bg-primary' },
            { label: 'Properties for Rent', value: `${rentCount} Units`, pct: rentCount > 0 ? Math.min(100, Math.round(rentCount / Math.max(saleCount + rentCount, 1) * 100)) : 0, color: 'bg-accent' },
            { label: 'Lead Conversion', value: `${leadStats?.converted || 0} Converted`, pct: conversionRate, color: 'bg-primary' },
          ].map(item => (
            <div key={item.label}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-medium">{item.label}</span>
                <span className="text-muted-foreground text-xs">{item.value}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.pct}%` }} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
